import React, { useState } from 'react';
import { Upload, Loader2, CheckCircle2, AlertCircle, PlusCircle, FileText, Sparkles, X, Scale } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { receptionsAPI } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { toast } from 'sonner';

/**
 * Dialog to import a Carrefour monthly PDF (multiple invoices).
 * Flow: choose file + site → preview (AI extraction) → confirm.
 */
export default function CarrefourImportDialog({ open, onClose, sites = [], onImported }) {
    const [siteId, setSiteId] = useState('');
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [progress, setProgress] = useState('');
    const [percent, setPercent] = useState(0);
    const [preview, setPreview] = useState(null);
    const [selectedInvoices, setSelectedInvoices] = useState({}); // id → bool
    const [createFlags, setCreateFlags] = useState({}); // `${invIdx}-${itemIdx}` → bool

    const reset = () => {
        setSiteId('');
        setFile(null);
        setPreview(null);
        setProgress('');
        setPercent(0);
        setSelectedInvoices({});
        setCreateFlags({});
    };

    const applyWeightCorrection = (invIdx, j) => {
        const inv = preview.invoices[invIdx];
        const it = inv.items[j];
        if (!it.weighed_suggestion) return;
        const s = it.weighed_suggestion;
        const newQty = s.quantity_corrected;
        const newPu = s.pu_ref;
        const copy = JSON.parse(JSON.stringify(preview));
        copy.invoices[invIdx].items[j] = {
            ...it,
            quantity: newQty,
            unit_price_ht: newPu,
            total_line_ht: Math.round(newQty * newPu * 100) / 100,
            weighed_suggestion: null, // consumed
        };
        setPreview(copy);
    };

    const applyAllWeightCorrections = () => {
        const copy = JSON.parse(JSON.stringify(preview));
        let count = 0;
        copy.invoices.forEach(inv => inv.items.forEach((it, j) => {
            if (it.weighed_suggestion) {
                const s = it.weighed_suggestion;
                inv.items[j] = {
                    ...it,
                    quantity: s.quantity_corrected,
                    unit_price_ht: s.pu_ref,
                    total_line_ht: Math.round(s.quantity_corrected * s.pu_ref * 100) / 100,
                    weighed_suggestion: null,
                };
                count++;
            }
        }));
        setPreview(copy);
        if (count > 0) toast.success(`${count} ligne(s) corrigée(s)`);
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    const handleAnalyze = async () => {
        if (!file || !siteId) {
            toast.error('Choisissez un PDF et un site');
            return;
        }
        setLoading(true);
        setProgress('Envoi du PDF...');
        setPercent(1);
        try {
            const fd = new FormData();
            fd.append('pdf', file);
            fd.append('site_id', siteId);
            const startRes = await receptionsAPI.importCarrefourPreview(fd);
            const jobId = startRes.data.job_id;
            if (!jobId) throw new Error('Pas de job_id retourné');

            // Poll the status until done/error (timeout: 5 min)
            setProgress('Extraction IA en cours...');
            const startTime = Date.now();
            const TIMEOUT_MS = 5 * 60 * 1000;
            let result = null;
            while (Date.now() - startTime < TIMEOUT_MS) {
                await new Promise(r => setTimeout(r, 1500));
                const stRes = await receptionsAPI.importCarrefourPreviewStatus(jobId);
                const st = stRes.data;
                if (st.progress) setProgress(st.progress);
                if (typeof st.percent === 'number') setPercent(st.percent);
                if (st.status === 'done') {
                    result = st.result;
                    setPercent(100);
                    break;
                }
                if (st.status === 'error') {
                    throw new Error(st.error || 'Erreur inconnue');
                }
            }
            if (!result) throw new Error("Délai d'extraction dépassé (5 min)");

            setPreview(result);
            const sel = {};
            const flags = {};
            result.invoices.forEach((inv, i) => {
                sel[i] = !inv.already_imported;
                inv.items.forEach((it, j) => {
                    flags[`${i}-${j}`] = it.match_status === 'new';
                });
            });
            setSelectedInvoices(sel);
            setCreateFlags(flags);
            toast.success(`${result.invoices.length} facture(s) détectée(s)`);
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message || 'Erreur d\'analyse');
        } finally {
            setLoading(false);
            setProgress('');
            setPercent(0);
        }
    };

    const handleConfirm = async () => {
        if (!preview) return;
        const payload = {
            supplier_id: preview.supplier_id,
            site_id: preview.site_id,
            invoices: preview.invoices
                .filter((_, i) => selectedInvoices[i])
                .map((inv, invIdxOrig) => {
                    // Need original index for createFlags lookup
                    const invIdx = preview.invoices.indexOf(inv);
                    return {
                        invoice_number: inv.invoice_number,
                        invoice_date: inv.invoice_date,
                        items: inv.items
                            .filter((it, j) => it.matched_supplier_product_id || createFlags[`${invIdx}-${j}`])
                            .map((it) => {
                                const j = inv.items.indexOf(it);
                                return {
                                    product_ref: it.product_ref,
                                    description: it.description,
                                    vat_rate: it.vat_rate,
                                    unit_price_ht: it.unit_price_ht,
                                    quantity: it.quantity,
                                    matched_supplier_product_id: it.matched_supplier_product_id || null,
                                    create_new: !it.matched_supplier_product_id && createFlags[`${invIdx}-${j}`],
                                };
                            }),
                    };
                }),
        };
        setLoading(true);
        try {
            const res = await receptionsAPI.importCarrefourConfirm(payload);
            toast.success(`${res.data.created_count} réception(s) créée(s), ${res.data.created_products_count} nouveau(x) produit(s)`);
            if (onImported) onImported();
            handleClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur d\'import');
        } finally {
            setLoading(false);
        }
    };

    const statusBadge = (s) => {
        if (s === 'exact') return <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[10px] font-semibold"><CheckCircle2 className="w-3 h-3" />Reconnu</span>;
        if (s === 'fuzzy') return <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 text-[10px] font-semibold"><AlertCircle className="w-3 h-3" />Similaire</span>;
        return <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-50 text-red-700 text-[10px] font-semibold"><PlusCircle className="w-3 h-3" />Nouveau</span>;
    };

    const selectedCount = Object.values(selectedInvoices).filter(Boolean).length;

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
            <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col" data-testid="carrefour-import-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-orange-500" />
                        Importer une facture Carrefour (PDF mensuel)
                    </DialogTitle>
                    <DialogDescription>
                        Le PDF est analysé par IA et chaque facture est convertie en réception manuelle distincte.
                    </DialogDescription>
                </DialogHeader>

                {!preview ? (
                    <div className="space-y-4 py-4">
                        <div className="space-y-1">
                            <Label>Site Fuji Express concerné *</Label>
                            <Select value={siteId} onValueChange={setSiteId}>
                                <SelectTrigger data-testid="carrefour-site-select"><SelectValue placeholder="Choisir un site" /></SelectTrigger>
                                <SelectContent>
                                    {sites.map(s => (
                                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1">
                            <Label>Fichier PDF Carrefour *</Label>
                            <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-orange-400 transition-colors">
                                <input
                                    type="file"
                                    accept=".pdf"
                                    className="hidden"
                                    id="carrefour-pdf-input"
                                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                                />
                                <label htmlFor="carrefour-pdf-input" className="cursor-pointer flex flex-col items-center gap-2">
                                    <Upload className="w-10 h-10 text-slate-400" />
                                    {file ? (
                                        <span className="font-semibold text-slate-700">{file.name}</span>
                                    ) : (
                                        <span className="text-sm text-slate-500">Cliquer pour sélectionner un PDF</span>
                                    )}
                                </label>
                            </div>
                        </div>
                        {loading && (
                            <div className="space-y-2 px-3 py-3 bg-orange-50 border border-orange-200 rounded-lg" data-testid="carrefour-import-progress">
                                <div className="flex items-center gap-2 text-sm text-orange-800">
                                    <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
                                    <span className="flex-1 truncate" data-testid="carrefour-import-progress-text">
                                        {progress || 'Traitement en cours...'}
                                    </span>
                                    <span className="font-bold tabular-nums text-orange-700">{percent}%</span>
                                </div>
                                <div className="h-2 w-full bg-orange-100 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-gradient-to-r from-orange-400 to-orange-600 transition-all duration-300 ease-out"
                                        style={{ width: `${Math.max(percent, 2)}%` }}
                                        data-testid="carrefour-import-progress-bar"
                                    />
                                </div>
                            </div>
                        )}
                        <DialogFooter>
                            <Button variant="outline" onClick={handleClose}>Annuler</Button>
                            <Button onClick={handleAnalyze} disabled={!file || !siteId || loading} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="analyze-pdf-btn">
                                {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Analyse IA…</> : <><Sparkles className="w-4 h-4 mr-2" />Analyser</>}
                            </Button>
                        </DialogFooter>
                    </div>
                ) : (
                    <>
                        <div className="flex-shrink-0 flex items-center gap-3 py-2 border-b text-sm">
                            <span className="font-semibold">{preview.supplier_name}</span>
                            <span className="text-slate-400">→</span>
                            <span className="font-semibold">{preview.site_name}</span>
                            <span className="ml-auto text-slate-500">
                                {selectedCount} / {preview.invoices.length} factures sélectionnées
                            </span>
                        </div>
                        <div className="flex-1 overflow-y-auto space-y-3 py-2">
                            {preview.invoices.map((inv, invIdx) => (
                                <div key={invIdx} className={`border rounded-lg ${selectedInvoices[invIdx] ? 'border-emerald-200' : 'border-slate-200 opacity-60'}`}>
                                    <div className="flex items-center gap-3 p-3 bg-slate-50 border-b">
                                        <Checkbox
                                            checked={!!selectedInvoices[invIdx]}
                                            onCheckedChange={(v) => setSelectedInvoices(prev => ({ ...prev, [invIdx]: !!v }))}
                                            data-testid={`select-invoice-${invIdx}`}
                                        />
                                        <FileText className="w-4 h-4 text-slate-500" />
                                        <span className="font-mono font-semibold">{inv.invoice_number}</span>
                                        <span className="text-slate-500">{inv.invoice_date}</span>
                                        <span className="ml-auto font-bold text-emerald-600">{formatCurrency(inv.total_ht)} HT</span>
                                        {inv.already_imported && (
                                            <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-700 text-xs font-semibold">Déjà importée</span>
                                        )}
                                    </div>
                                    <table className="w-full text-xs">
                                        <thead className="text-slate-500">
                                            <tr>
                                                <th className="text-left px-3 py-1">Référence</th>
                                                <th className="text-left px-3 py-1">Produit</th>
                                                <th className="text-right px-3 py-1">TVA</th>
                                                <th className="text-right px-3 py-1">PU HT</th>
                                                <th className="text-right px-3 py-1">Qté</th>
                                                <th className="text-right px-3 py-1">Total HT</th>
                                                <th className="text-center px-3 py-1">Statut</th>
                                                <th className="text-center px-3 py-1">Créer ?</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {inv.items.map((it, j) => (
                                                <tr key={j} className="border-t">
                                                    <td className="px-3 py-1 font-mono text-slate-500">{it.product_ref}</td>
                                                    <td className="px-3 py-1">
                                                        {it.matched_product_name ? (
                                                            <>
                                                                <div className="text-emerald-700">{it.matched_product_name}</div>
                                                                <div className="text-[10px] text-slate-400">{it.description}</div>
                                                            </>
                                                        ) : (
                                                            <span>{it.description}</span>
                                                        )}
                                                    </td>
                                                    <td className="px-3 py-1 text-right">{it.vat_rate}%</td>
                                                    <td className="px-3 py-1 text-right">{formatCurrency(it.unit_price_ht)}</td>
                                                    <td className="px-3 py-1 text-right font-semibold">
                                                        {it.quantity}
                                                        {it.weighed_suggestion && (
                                                            <div className="text-[10px] text-orange-600 font-normal italic">
                                                                → {it.weighed_suggestion.quantity_corrected} kg
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="px-3 py-1 text-right font-semibold">{formatCurrency(it.total_line_ht)}</td>
                                                    <td className="px-3 py-1 text-center">
                                                        <div className="flex flex-col items-center gap-0.5">
                                                            {statusBadge(it.match_status)}
                                                            {it.weighed_suggestion && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => applyWeightCorrection(invIdx, j)}
                                                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-orange-100 hover:bg-orange-200 text-orange-700 text-[10px] font-semibold"
                                                                    title={`Corriger : ${it.weighed_suggestion.quantity_corrected} kg × ${it.weighed_suggestion.pu_ref} €`}
                                                                >
                                                                    <Scale className="w-3 h-3" />
                                                                    Pesée caisse auto
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="px-3 py-1 text-center">
                                                        {!it.matched_supplier_product_id && (
                                                            <Checkbox
                                                                checked={!!createFlags[`${invIdx}-${j}`]}
                                                                onCheckedChange={(v) => setCreateFlags(prev => ({ ...prev, [`${invIdx}-${j}`]: !!v }))}
                                                            />
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ))}
                        </div>
                        <DialogFooter className="flex-shrink-0 border-t pt-3">
                            <Button variant="outline" onClick={handleClose}>Annuler</Button>
                            <Button variant="outline" onClick={reset}>
                                <X className="w-4 h-4 mr-2" />Changer de PDF
                            </Button>
                            {preview.invoices.some(inv => inv.items.some(it => it.weighed_suggestion)) && (
                                <Button
                                    variant="outline"
                                    onClick={applyAllWeightCorrections}
                                    className="border-orange-300 text-orange-700 hover:bg-orange-50"
                                    data-testid="correct-all-weights-btn"
                                >
                                    <Scale className="w-4 h-4 mr-2" />
                                    Corriger les pesées caisse auto
                                </Button>
                            )}
                            <Button
                                onClick={handleConfirm}
                                disabled={selectedCount === 0 || loading}
                                className="bg-emerald-500 hover:bg-emerald-600 text-white"
                                data-testid="confirm-import-btn"
                            >
                                {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Import…</> : <><CheckCircle2 className="w-4 h-4 mr-2" />Importer {selectedCount} facture(s)</>}
                            </Button>
                        </DialogFooter>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}
